"use client";

import {
  ArrowUpRight,
  Globe,
  RefreshCcw,
  Search,
  ShoppingBag,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { SectionEyebrow, SectionTitle } from "../section-eyebrow";
import {
  CornerFrame,
  ScrollReveal,
  ScrollStagger,
  StaggerItem,
} from "../scroll-reveal";

const ICONS = [Globe, ShoppingBag, RefreshCcw, Search];

export function Services() {
  const t = useTranslations("services");
  const items = t.raw("items") as Array<{
    title: string;
    description: string;
    tag: string;
  }>;

  return (
    <section
      id="services"
      className="relative py-12 sm:py-20 lg:py-28"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <ScrollReveal as="header" className="mb-10 flex flex-col gap-5 sm:max-w-2xl lg:mb-14">
          <div className="flex items-center justify-between">
            <SectionEyebrow>{t("eyebrow")}</SectionEyebrow>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80 hidden sm:inline">
              / 04 prestations
            </span>
          </div>
          <SectionTitle text={t("title")} italicLastWords={2} />
          <p data-blend className="text-base sm:text-lg">
            {t("subtitle")}
          </p>
        </ScrollReveal>

        <ScrollStagger
          className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl bg-border ring-1 ring-border md:grid-cols-2"
          staggerChildren={0.08}
        >
          {items.map((item, i) => {
            const Icon = ICONS[i] ?? Globe;
            return (
              <StaggerItem
                key={item.title}
                as="article"
                className="group relative flex flex-col gap-4 bg-card p-5 transition-colors hover:bg-muted/40 sm:p-9"
              >
                <span
                  aria-hidden
                  className="absolute inset-0 bg-gradient-to-br from-aurora-cyan/0 via-transparent to-aurora-violet/0 opacity-0 transition-opacity duration-500 group-hover:opacity-[0.04]"
                />
                <div className="relative flex items-start justify-between gap-4">
                  <div className="grid size-11 place-items-center rounded-xl border border-border bg-background transition-all duration-300 group-hover:border-aurora-cyan/40 group-hover:shadow-[0_0_18px_-6px_var(--aurora-cyan)]">
                    <Icon className="size-4 text-foreground" aria-hidden />
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      / {String(i + 1).padStart(2, "0")}
                    </span>
                    <ArrowUpRight
                      className="size-4 text-muted-foreground transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground rtl:-scale-x-100"
                      aria-hidden
                    />
                  </div>
                </div>
                <div className="relative mt-2 flex flex-1 flex-col gap-2.5">
                  <h3 className="font-sans text-xl font-medium tracking-tight text-foreground">
                    {item.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {item.description}
                  </p>
                </div>
                <div className="relative mt-2">
                  <span className="inline-flex items-center rounded-full border border-border bg-background px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    {item.tag}
                  </span>
                </div>
              </StaggerItem>
            );
          })}
        </ScrollStagger>
      </div>
    </section>
  );
}
